import React, { useEffect, useState } from 'react';
import {
  Form, Schema, Stack, IconButton, Divider, FormInstance, SelectPicker, DatePicker, Grid, Row, Col, NumberInput, Toggle,
} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import PageContent from '@/components/PageContent';
import ErrorMessage from '@/components/ErrorMessage';
import Textarea from '@/components/Textarea';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import MapSelector from '@/components/MapSelector';
import MultilingualInput from '@/components/MultiLingualInput';
import MultilingualTextarea from '@/components/MultiLingualTextarea';
import { multiLingualRule } from '@/utils/multilingual-rule';
import { BaseErrorModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import { EnergyHub, EnergyHubCategoryList, EnergyHubStatus, EnergyHubStatusList, UpsertEnergyHub } from '@/models/energy-hub.model';
import { upsertEnergyHub } from '@/services/energy-hub.service';
import { getCitiesLookup } from '@/services/city.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const schema = Schema.Model<UpsertEnergyHub>({
  name: multiLingualRule(200, true),
  description: multiLingualRule(4000),
  category: Schema.Types.NumberType().isRequired('This field is required.'),
  address: multiLingualRule(500),
  lat: Schema.Types.NumberType().isRequired('Pick the location on the map.').range(-90, 90),
  lng: Schema.Types.NumberType().isRequired('Pick the location on the map.').range(-180, 180),
  openingHours: Schema.Types.StringType().maxLength(1000),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  checkInFee: Schema.Types.NumberType().isRequired('This field is required.').range(0, 1000),
  purchaseFee: Schema.Types.NumberType().isRequired('This field is required.').range(0, 1000),
  contactName: Schema.Types.StringType().maxLength(200),
  contactPhone: Schema.Types.StringType().maxLength(50),
  contactEmail: Schema.Types.StringType().isEmail('Not a valid email.').maxLength(200),
  adminNote: Schema.Types.StringType().maxLength(4000),
});

const emptyModel: UpsertEnergyHub = {
  id: undefined,
  name: {},
  category: undefined,
  status: EnergyHubStatus.Draft,
  isFoundingHub: false,
  freeUntil: undefined,
  checkInFee: 1,
  purchaseFee: 3,
};

const toUpsert = (h: EnergyHub): UpsertEnergyHub => ({
  id: h.id,
  name: h.name,
  description: h.description ?? {},
  category: h.category,
  address: h.address ?? {},
  lat: h.lat,
  lng: h.lng,
  cityId: h.cityId,
  logoUrl: h.logoUrl,
  coverImageUrl: h.coverImageUrl,
  openingHours: h.openingHours,
  status: h.status,
  isFoundingHub: h.isFoundingHub,
  freeUntil: h.freeUntil,
  checkInFee: h.checkInFee,
  purchaseFee: h.purchaseFee,
  contactName: h.contactName,
  contactPhone: h.contactPhone,
  contactEmail: h.contactEmail,
  adminNote: h.adminNote,
});

/** Empty strings are sent as null so optional columns stay empty. */
const clean = (v?: string) => (v && v.trim() ? v : undefined);

interface Props {
  formObject?: EnergyHub;
  pageState: PageState;
  onSaved?: () => void;
}

const EnergyHubForm: React.FC<Props> = ({ formObject, pageState, onSaved }) => {
  const [formValue, setFormValue] = useState<UpsertEnergyHub>(emptyModel);
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    setFormValue(pageState == PageState.Edit && formObject ? toUpsert(formObject) : emptyModel);
  }, [formObject, pageState]);

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = await upsertEnergyHub({
        ...formValue,
        openingHours: clean(formValue.openingHours),
        contactName: clean(formValue.contactName),
        contactPhone: clean(formValue.contactPhone),
        contactEmail: clean(formValue.contactEmail),
        adminNote: clean(formValue.adminNote),
        logoUrl: clean(formValue.logoUrl),
        coverImageUrl: clean(formValue.coverImageUrl),
        cityId: formValue.cityId ?? undefined,
        freeUntil: formValue.freeUntil ?? undefined,
        lat: Number(formValue.lat),
        lng: Number(formValue.lng),
        checkInFee: Number(formValue.checkInFee),
        purchaseFee: Number(formValue.purchaseFee),
      });
      if (result.success) {
        toastSuccessMessage('Energy hub saved');
        if (pageState == PageState.New && result.result?.id) {
          nav(`/energy-hubs/${result.result.id}`);
        } else {
          onSaved?.();
        }
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  const position = formValue.lat != null && formValue.lng != null
    ? { lat: Number(formValue.lat), lng: Number(formValue.lng) }
    : null;

  return (
    <PageContent>
      <Form name="upsert-energy-hub" fluid ref={formRef}
        onChange={value => setFormValue(pre => ({ ...pre, ...value }))}
        formValue={formValue}
        model={schema}
        disabled={loading}
      >
        <Grid fluid>
          <Row>
            <Col span={{ xs: 24, xl: 12 }}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control name="name" accepter={MultilingualInput} />
                <Form.HelpText>
                  Pick a language and type its text. The app shows the language of the hub's city (set on the City),
                  falling back to English.
                </Form.HelpText>
              </Form.Group>
              <Row>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Control name="category" accepter={SelectPicker} data={EnergyHubCategoryList} searchable={false} block />
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Control name="status" accepter={SelectPicker} data={EnergyHubStatusList} searchable={false} cleanable={false} block />
                    <Form.HelpText>Only Active hubs are listed in the app and accept check-ins.</Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control name="description" accepter={MultilingualTextarea} rows={3} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Address</Form.Label>
                <Form.Control name="address" accepter={MultilingualInput} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Opening hours</Form.Label>
                <Form.Control name="openingHours" accepter={Textarea} rows={2} placeholder="e.g. Mon–Fri 08:00–18:00, Sat–Sun 09:00–17:00" />
              </Form.Group>
              <Form.Group>
                <Form.Label>City (optional)</Form.Label>
                <Form.Control name="cityId" accepter={FetchingSelect} block
                  fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
                  placeholder="Select a city" />
              </Form.Group>

              <Divider>Billing</Divider>
              <Row>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Check-in fee (HK$)</Form.Label>
                    <Form.Control name="checkInFee" accepter={NumberInput} min={0} step={0.5} />
                    <Form.HelpText>Charged per club runner who checks in on a Hub Run.</Form.HelpText>
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Purchase fee (HK$)</Form.Label>
                    <Form.Control name="purchaseFee" accepter={NumberInput} min={0} step={0.5} />
                    <Form.HelpText>Total per verified buyer; the check-in fee already paid is deducted.</Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Founding hub</Form.Label>
                    <Form.Control name="isFoundingHub" accepter={Toggle} />
                    <Form.HelpText>Badge only. The free period is set by "Free until".</Form.HelpText>
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Free until (optional)</Form.Label>
                    <Form.Control name="freeUntil" accepter={DatePicker} format="yyyy-MM-dd HH:mm" placement="autoVerticalStart" block />
                    <Form.HelpText>Fees before this time are recorded as waived (founding hubs: 3 months).</Form.HelpText>
                  </Form.Group>
                </Col>
              </Row>

              <Divider>Venue contact</Divider>
              <Row>
                <Col span={{ xs: 24, md: 8 }}>
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control name="contactName" />
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 8 }}>
                  <Form.Group>
                    <Form.Label>Phone / WhatsApp</Form.Label>
                    <Form.Control name="contactPhone" />
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 8 }}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control name="contactEmail" />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Admin note (internal)</Form.Label>
                <Form.Control name="adminNote" accepter={Textarea} rows={3} />
              </Form.Group>
            </Col>

            <Col span={{ xs: 24, xl: 12 }}>
              <Row>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control name="lat" accepter={NumberInput} />
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 12 }}>
                  <Form.Group>
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control name="lng" accepter={NumberInput} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Location (click the map; check-ins must be within 500 m)</Form.Label>
                <MapSelector
                  value={position}
                  onChange={pos => setFormValue(pre => ({ ...pre, lat: pos.lat, lng: pos.lng }))}
                  defaultCenter={position ?? undefined}
                  height="380px"
                />
              </Form.Group>
              <Row>
                <Col span={{ xs: 24, md: 8 }}>
                  <Form.Group>
                    <Form.Label>Logo</Form.Label>
                    <ImageUploader
                      height="180px"
                      url={formValue.logoUrl}
                      objectStorageCategory={ObjectStorageFileCategory.EnergyHub}
                      onChange={url => setFormValue(pre => ({ ...pre, logoUrl: url }))}
                    />
                  </Form.Group>
                </Col>
                <Col span={{ xs: 24, md: 16 }}>
                  <Form.Group>
                    <Form.Label>Cover image</Form.Label>
                    <ImageUploader
                      height="180px"
                      url={formValue.coverImageUrl}
                      objectStorageCategory={ObjectStorageFileCategory.EnergyHub}
                      onChange={url => setFormValue(pre => ({ ...pre, coverImageUrl: url }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
        </Grid>
      </Form>
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      <Divider />
      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav('/energy-hubs')}>Back</IconButton>
        <IconButton icon={<PageNextIcon />} placement="right" appearance="primary" onClick={handleSubmit} loading={loading}>
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default EnergyHubForm;
